import React, { useEffect } from 'react';
import {
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Avatar,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import { useRef } from 'react';
import { FieldArray } from 'formik';
import AutoComplete from '../../../components/AutoComplete';
import { Flavor } from '../../../store/flavor/api/interface';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  findSelectShape,
  searchSelectShape,
} from '../../../store/selectShape/api';
import { setSkip as setSkipShape } from '../../../store/selectShape/selectShapeSlice';
import _, { uniqBy } from 'lodash';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';
import { getAssetURl } from '../../../utils/url';
import { isValidArray } from '../../../utils/valid';
import { Shape } from '../../../store/shape/api/interface';

interface IProps {
  isAddMode: boolean;
  response: Flavor | undefined;
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: any;
}

const DetailsForm: React.FC<IProps> = ({
  isAddMode,
  response,
  values,
  // handleChange,
  setFieldValue,
  errors,
  touched,
}) => {
  const arrayHelpersRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  // Shape Data
  const {
    data: shapeData,
    isLoading: isShapeLoading,
    skip: shapeSkip,
    hasMore: shapeHasMore,
    status: shapeStatus,
  } = useAppSelector((state) => state.selectShape);

  useEffect(() => {
    if (shapeStatus === 'idle') {
      dispatch(
        findSelectShape({
          activeOnly: true,
          limit: 50,
          skip: shapeSkip,
        })
      );
    }
  }, [shapeSkip, shapeStatus]);

  const onScrollFetchShape = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      shapeHasMore
    ) {
      dispatch(setSkipShape({ skip: shapeSkip + 10 }));
      dispatch(
        findSelectShape({
          activeOnly: true,
          limit: 50,
          skip: shapeSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchShape = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const params = {
      activeOnly: true,
      ...(!e.target.value && { limit: 50 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipShape({ skip: 0 }));
    dispatch(searchSelectShape(params));
  };

  const shapeDetails =
    response?.shapeImages?.map((item) => {
      return item?.shape;
    }) ?? [];

  const uniqueShapeData = uniqBy(
    [
      ...(!isAddMode && response?.shapeImages ? [...shapeDetails] : []),
      ...shapeData,
    ],
    'id'
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  useEffect(() => {
    if (isValidArray(shapeData) && isAddMode) {
      shapeData?.map((element: Shape, index: number) => {
        if (element?.id) {
          setFieldValue(`imageItems[${index}].shapeId`, element?.id);
          setFieldValue(`imageItems[${index}].top`, '');
          setFieldValue(`imageItems[${index}].front`, '');
        }
      });
    }
  }, [shapeData, isAddMode]);

  useEffect(() => {
    if (isValidArray(shapeData) && !isAddMode) {
      const valueShapeIds = _.map(values?.imageItems, 'shapeId');
      const mainShapeIds = _.map(shapeData, 'id');
      const missingShapeIds = _.difference(mainShapeIds, valueShapeIds);
      if (isValidArray(missingShapeIds)) {
        const newItems = missingShapeIds?.map((id) => ({
          shapeId: id,
          top: '',
          front: '',
        }));
        const updatedValueArray = _.concat(values?.imageItems, newItems);
        if (updatedValueArray.length !== values?.imageItems?.length) {
          setFieldValue(`imageItems`, updatedValueArray);
        }
      }
    }
  }, [shapeData, isAddMode, values?.imageItems]);

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        variant='h6'
        sx={{ color: 'text.disabled', mb: 2 }}
      >
        Image Items:
      </Typography>
      <Stack
        divider={
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed' }}
          />
        }
        spacing={3}
      >
        <FieldArray
          name='imageItems'
          render={(arrayHelpers: any) => {
            arrayHelpersRef.current = arrayHelpers;
            return (
              <>
                {values?.imageItems?.map((item: any, idx: any) => {
                  return (
                    <React.Fragment key={idx}>
                      <Stack
                        alignItems='flex-end'
                        spacing={1.5}
                      >
                        <Grid
                          container
                          spacing={3}
                        >
                          <Grid
                            item
                            xs={12}
                            sm={4}
                          >
                            <AutoComplete
                              onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                } else if (newValue) {
                                  setFieldValue(
                                    `imageItems[${idx}].shapeId`,
                                    newValue.id
                                  );
                                }
                              }}
                              onInputChange={(event, newInputValue) => {
                                if (!newInputValue) {
                                  setFieldValue(
                                    `imageItems[${idx}].shapeId`,
                                    ''
                                  );
                                }
                              }}
                              renderOption={(props, option) => (
                                <li {...props}>{option.labelEn}</li>
                              )}
                              getOptionLabel={(option) => option.labelEn}
                              error={Boolean(
                                touched?.imageItems?.[idx]?.shapeId &&
                                  errors?.imageItems?.[idx]?.shapeId
                              )}
                              helperText={
                                Boolean(
                                  touched?.imageItems?.[idx]?.shapeId &&
                                    errors?.imageItems?.[idx]?.shapeId
                                )
                                  ? errors.imageItems?.[idx]?.shapeId
                                  : ''
                              }
                              fetchFunction={onScrollFetchShape}
                              label={'Select Shape'}
                              loading={isShapeLoading}
                              options={uniqueShapeData}
                              searchFunction={searchShape}
                              value={
                                uniqueShapeData?.filter(
                                  (shape) =>
                                    shape?.id ===
                                    values.imageItems?.[idx]?.shapeId
                                )?.[0] || null
                              }
                              disabled={true}
                            />
                          </Grid>
                          {/* <Grid
                            item
                            xs={12}
                            sm={3}
                          >
                            <TextField
                              id='sliced'
                              name={`imageItems[${idx}].sliced`}
                              value={values?.imageItems[idx].sliced ?? ''}
                              onChange={handleChange}
                              label={'Sliced Image'}
                              fullWidth
                              variant='outlined'
                              error={Boolean(
                                touched?.imageItems?.[idx]?.sliced &&
                                  errors?.imageItems?.[idx]?.sliced
                              )}
                              helperText={
                                Boolean(
                                  touched?.imageItems?.[idx]?.sliced &&
                                    errors?.imageItems?.[idx]?.sliced
                                ) && errors.imageItems?.[idx]?.sliced
                              }
                            />
                          </Grid> */}

                          <Grid
                            item
                            xs={`${values?.imageItems?.[idx]?.top}` ? 8 : 12}
                            sm={`${values?.imageItems?.[idx]?.top}` ? 2.5 : 4}
                          >
                            <TextField
                              type='file'
                              fullWidth
                              name={`imageItems[${idx}].top`}
                              id='top'
                              onChange={(e) =>
                                handleFileChange(
                                  e as React.ChangeEvent<HTMLInputElement>,
                                  DocumentType.FlavorShapeTop,
                                  `imageItems[${idx}].top`
                                )
                              }
                              inputProps={{
                                multiple: false,
                                accept: 'image/*',
                              }}
                              InputLabelProps={{ shrink: true }}
                              variant='outlined'
                              label={'Top Image'}
                              error={Boolean(
                                touched?.imageItems?.[idx]?.top &&
                                  errors?.imageItems?.[idx]?.top
                              )}
                              helperText={
                                Boolean(
                                  touched?.imageItems?.[idx]?.top &&
                                    errors?.imageItems?.[idx]?.top
                                ) && errors.imageItems?.[idx]?.top
                              }
                            />
                          </Grid>

                          {values?.imageItems?.[idx]?.top && (
                            <Grid
                              item
                              xs={4}
                              sm={1.5}
                            >
                              <Avatar
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  values?.imageItems?.[idx]?.top,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  width: { xs: '75px' },
                                  height: 50,
                                }}
                              />
                            </Grid>
                          )}

                          <Grid
                            item
                            xs={`${values?.imageItems?.[idx]?.front}` ? 8 : 12}
                            sm={`${values?.imageItems?.[idx]?.front}` ? 2.5 : 4}
                          >
                            <TextField
                              type='file'
                              fullWidth
                              name={`imageItems[${idx}].front`}
                              id='front'
                              onChange={(e) =>
                                handleFileChange(
                                  e as React.ChangeEvent<HTMLInputElement>,
                                  DocumentType.FlavorShapeFront,
                                  `imageItems[${idx}].front`
                                )
                              }
                              inputProps={{
                                multiple: false,
                                accept: 'image/*',
                              }}
                              InputLabelProps={{ shrink: true }}
                              variant='outlined'
                              label={'Front Image'}
                              error={Boolean(
                                touched?.imageItems?.[idx]?.front &&
                                  errors?.imageItems?.[idx]?.front
                              )}
                              helperText={
                                Boolean(
                                  touched?.imageItems?.[idx]?.front &&
                                    errors?.imageItems?.[idx]?.front
                                ) && errors.imageItems?.[idx]?.front
                              }
                            />
                          </Grid>

                          {values?.imageItems?.[idx]?.front && (
                            <Grid
                              item
                              xs={4}
                              sm={1.5}
                            >
                              <Avatar
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  values?.imageItems?.[idx]?.front,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  width: { xs: '75px' },
                                  height: 50,
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                        {
                          <div>
                            {idx + 1 === values?.imageItems?.length && (
                              <Button
                                size='small'
                                startIcon={<Iconify icon='eva:plus-fill' />}
                                disabled={true}
                                onClick={() =>
                                  arrayHelpersRef.current.push({
                                    shapeId: '',
                                    top: '',
                                    front: '',
                                  })
                                }
                                sx={{
                                  flexShrink: 0,
                                  paddingRight: '15px',
                                }}
                              >
                                New image item
                              </Button>
                            )}
                            <Button
                              size='small'
                              color='error'
                              startIcon={<Iconify icon='eva:trash-2-outline' />}
                              onClick={() => {
                                arrayHelpers?.remove(idx);
                              }}
                              disabled={true}
                            >
                              Remove
                            </Button>
                          </div>
                        }
                      </Stack>
                      <Divider
                        sx={{
                          my: 3,
                          borderStyle: 'dashed',
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              </>
            );
          }}
        ></FieldArray>
      </Stack>
    </Box>
  );
};
export default DetailsForm;
